import React, { useState, useEffect } from 'react';
import { Calendar, Target, TrendingUp, Bell, Star, CheckCircle, Clock, Award, Plus, MessageSquare, Share2, DollarSign, Users, BookOpen, AlertTriangle } from 'lucide-react';
import { useUserData } from '@/hooks/useUserData';
import { useFinanceData } from '@/hooks/useFinanceData';
import { useCashpurrNotifications } from '@/hooks/useCashpurrNotifications';
import { dailyTaskService } from '@/services/dailyTaskService';
import { Button } from '@/components/ui/button';
import TutorialSystem from '@/components/tutorial/TutorialSystem';
import { useNavigate } from 'react-router-dom';
import { AllTasksView } from '@/components/dashboard/all-tasks-view';
import { FinanceCard } from './finance-card';
import { DailyTaskNotification } from './daily-task-notification';
import CashpurrNotificationPopup from '@/components/ui/cashpurr-notification-popup';

interface DashboardProps {
  // Remove userProgress prop since we'll use real data
}

const Dashboard: React.FC<DashboardProps> = () => {
  // Live user data from Supabase via dataService
  const {
    profile,
    tasks,
    sprintDay,
    streakDays,
    daysRemaining,
    completedTasksCount,
    pendingTasksCount,
    loading,
    error,
    toggleTask,
    addTask,
    notifications,
    socialActivities,
    markNotificationRead,
  } = useUserData();

  const { financeData, loading: financeLoading } = useFinanceData();
  const { showPopup, currentTask, handleClosePopup } = useCashpurrNotifications();

  const [showTutorial, setShowTutorial] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [todaysDailyTasks, setTodaysDailyTasks] = useState([]);
  const navigate = useNavigate();

  const handleViewReport = () => {
    navigate('/app/roi-tracker');
  };

  useEffect(() => {
    // Show tutorial for new users
    if (profile && !profile.tutorial_completed) {
      setShowTutorial(true);
    }
  }, [profile]);

  // Load daily tasks
  useEffect(() => {
    const loadDailyTasks = async () => {
      if (!profile?.user_id) return;
      try {
        await dailyTaskService.createDailyTasksIfNeeded(profile.user_id);
        const daily = await dailyTaskService.getTodaysDailyTasks(profile.user_id);
        setTodaysDailyTasks(daily || []);
      } catch (error) {
        console.error('Error loading daily tasks:', error);
      }
    };
    loadDailyTasks();
  }, [profile?.user_id]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-startup-teal mx-auto mb-4"></div>
          <p className="text-startup-gray">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-startup-red mb-4">Error loading dashboard: {error}</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  // Show all tasks view if requested
  if (showAllTasks) {
    // Merge daily tasks with regular tasks for the all tasks view
    const allTasksForAllView = [...tasks, ...todaysDailyTasks.map(dt => ({
      id: dt.id,
      user_id: dt.user_id,
      title: dt.title,
      description: dt.description,
      completed: dt.completed,
      due_date: dt.due_date,
      priority: dt.priority,
      category: dt.category,
      created_at: dt.created_at,
      updated_at: dt.updated_at,
      sprint_day: null
    }))];
    
    return (
      <AllTasksView 
        tasks={allTasksForAllView}
        onTaskToggle={toggleTask}
        onClose={() => setShowAllTasks(false)}
      />
    );
  }

  // Calculate task completion percentage to match View All Tasks tab
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayDateStr = today.toISOString().split('T')[0];
  
  // Count all tasks for percentage calculation to match AllTasksView
  // Remove duplicates by creating a Map with task ID as key
  const taskMap = new Map();
  
  // Add regular tasks first
  tasks.forEach(task => {
    taskMap.set(task.id, task);
  });
  
  // Add daily tasks, but only if they don't already exist
  todaysDailyTasks.forEach(dt => {
    if (!taskMap.has(dt.id)) {
      taskMap.set(dt.id, {
        id: dt.id,
        user_id: dt.user_id,
        title: dt.title,
        description: dt.description,
        completed: dt.completed,
        due_date: dt.due_date,
        priority: dt.priority,
        category: dt.category,
        created_at: dt.created_at,
        updated_at: dt.updated_at,
        sprint_day: null
      });
    }
  });
  
  // Convert back to array
  const allTasksWithDaily = Array.from(taskMap.values());
  
  const totalTasks = allTasksWithDaily.length;
  const completedTasks = allTasksWithDaily.filter(task => task.completed).length;
  const sprintDayPercentage = (sprintDay / 30) * 100;
  const progressPercentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
  const taskCompletionPercentage = Math.round(progressPercentage);
  
  // Get today's pending tasks - same logic as Boss Mode and All Tasks View
  // This should match exactly what's shown in other views
  const todaysPendingTasks = allTasksWithDaily.filter(task => {
    if (task.completed) return false;
    
    // Check if task has a due date and it's today
    if (task.due_date) {
      const taskDateStr = new Date(task.due_date).toISOString().split('T')[0];
      return taskDateStr === todayDateStr;
    }
    
    // For tasks without due date, check if they were created today (like daily tasks)
    if (task.created_at) {
      const createdDateStr = new Date(task.created_at).toISOString().split('T')[0];
      return createdDateStr === todayDateStr;
    }
    
    return false;
  });

  // Get overdue tasks (past due date, not completed, excluding today's tasks)
  const overdueTasks = allTasksWithDaily.filter(task => {
    if (!task.due_date || task.completed) return false;
    const taskDateStr = new Date(task.due_date).toISOString().split('T')[0];
    return taskDateStr < todayDateStr; // Only tasks with past due dates (not today)
  });
  
  // For "Your Tasks" tab, show all pending tasks, limit to 10
  const allPendingTasks = allTasksWithDaily.filter(task => !task.completed);
  const pendingTasks = allPendingTasks.slice(0, 10);
  
  // Get today's pending social activities
  const pendingSocialActivities = socialActivities?.filter(activity => {
    if (activity.completed) return false;
    
    // Check if activity is due today
    if (activity.due_date) {
      const activityDateStr = new Date(activity.due_date).toISOString().split('T')[0];
      return activityDateStr === todayDateStr;
    }
    
    return false;
  }) || [];

  // Generate dynamic badges based on user progress
  const badges = [];
  if (streakDays >= 3) badges.push('Streak Champion');
  if (completedTasksCount >= 10) badges.push('Task Master');
  if (sprintDay >= 7) badges.push('Week Warrior');
  if (taskCompletionPercentage >= 50) badges.push('Progress Pro');
  if (pendingSocialActivities.length === 0 && socialActivities?.length > 0) badges.push('Social Media Pro');

  // Destructure with defaults to avoid undefined errors
  const {
    revenue = 0,
    expenses = 0,
    profit = 0,
    revenueChange = 0,
    expensesChange = 0
  } = financeData || {};

  const formatTime = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString();
  };

  const quickLinks = [
    { 
      title: 'Idea Assessment', 
      icon: Target, 
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      description: 'Validate your business ideas',
      href: '/app/idea-validation' 
    },
    { 
      title: 'Boss Mode', 
      icon: Award, 
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      description: 'Daily tasks & progress',
      href: '/app/boss-mode' 
    },
    { 
      title: 'ROI Tracker', 
      icon: TrendingUp, 
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      description: 'Track your investments',
      href: '/app/roi-tracker' 
    },
    { 
      title: 'Social Tracker', 
      icon: Share2, 
      iconBg: 'bg-pink-100',
      iconColor: 'text-pink-600',
      description: 'Monitor social presence',
      href: '/app/social-tracker' 
    },
    { 
      title: 'Community', 
      icon: Users, 
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      description: 'Connect with others',
      href: '/app/community-pods' 
    },
    { 
      title: 'Journal', 
      icon: BookOpen, 
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      description: 'Document your journey',
      href: '/app/founder-journal' 
    },
  ];

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) return;
    await addTask({
      title: newTaskTitle,
      description: '',
      completed: false,
      priority: 'medium',
      category: 'general',
    });
    setNewTaskTitle('');
  };

  return (
    <div className="p-6" data-tutorial="dashboard">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="animate-fade-in">
          <h1 className="text-3xl font-bold text-startup-navy mb-2">
            Welcome back, {profile?.full_name || 'Founder'}! 🚀
          </h1>
          <p className="text-startup-gray">
            Day {sprintDay} of your 30-day cash sprint • <span className="font-semibold text-startup-red">{daysRemaining} days remaining</span>
          </p>
        </div>

        {/* Hero Progress Card */}
        <div className="card-startup animate-fade-in bg-gradient-startup text-white overflow-hidden relative">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">30-Day Sprint Progress</h2>
                <p className="text-white/80">
                  {sprintDay <= 5 ? "Great start! Building momentum..." : 
                   sprintDay <= 15 ? "You're crushing it! Keep the momentum going." :
                   sprintDay <= 25 ? "Almost there! The finish line is in sight." :
                   "Final sprint! Your first customer awaits!"}
                </p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold">{Math.round(progressPercentage)}%</div>
                <div className="text-white/80">Complete</div>
                <div className="text-lg font-semibold text-startup-gold mt-1">
                  {daysRemaining} days left
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="bg-white/20 rounded-full h-4 overflow-hidden">
                <div 
                  className="bg-white h-full rounded-full animate-progress-fill transition-all duration-1500 ease-out"
                  style={{ '--progress-width': `${progressPercentage}%` } as React.CSSProperties}
                />
              </div>
              <div className="flex justify-between text-sm text-white/80 mt-2">
                <span>Day {sprintDay}</span>
                <span>Day 30</span>
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold">{streakDays}</div>
                <div className="text-white/80 text-sm">Day Streak</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{completedTasksCount}</div>
                <div className="text-white/80 text-sm">Tasks Done</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{badges.length}</div>
                <div className="text-white/80 text-sm">Badges Earned</div>
              </div>
            </div>

            {/* Badges Row */}
            <div className="flex gap-2 mt-4 flex-wrap">
              {badges.length > 0 ? badges.map((badge, index) => (
                <div 
                  key={badge}
                  className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm animate-pulse-gold"
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  <Award className="w-4 h-4 inline mr-1" />
                  {badge}
                </div>
              )) : (
                <div className="text-white/60 text-sm">
                  Complete tasks to earn your first badge! 🏆
                </div>
              )}
            </div>
          </div>

        {/* Cashpurr Mascot */}
        <div className="absolute -right-4 -bottom-4 opacity-20">
          <div className="w-32 h-32 bg-white/10 rounded-full animate-float" />
        </div>
      </div>

      {/* Today's Pending Tasks Section */}
      {todaysPendingTasks.length > 0 && (
          <div className="card-startup animate-fade-in border-2 border-startup-teal/30 bg-gradient-to-r from-startup-teal/5 to-startup-teal/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-startup-navy flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-startup-teal" />
                Today's Pending Tasks
              </h3>
              <span className="bg-startup-teal text-white px-3 py-1 rounded-full text-sm font-medium animate-pulse">
                {todaysPendingTasks.length} due today
              </span>
            </div>
            
            <div className="space-y-3">
              {todaysPendingTasks.map((task, index) => (
                <div 
                  key={task.id}
                  className="flex items-center justify-between p-3 bg-white/50 border border-startup-teal/20 rounded-xl hover:shadow-md hover:border-startup-teal/40 transition-all duration-300 animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => toggleTask(task.id)}
                      className="w-5 h-5 rounded-full border-2 border-startup-teal hover:bg-startup-teal hover:border-startup-teal flex items-center justify-center transition-colors group"
                    >
                      {task.completed && <CheckCircle className="w-3 h-3 text-white" />}
                      {!task.completed && <div className="w-2 h-2 rounded-full bg-startup-teal opacity-0 group-hover:opacity-100 transition-opacity" />}
                    </button>
                    <div className={`w-3 h-3 rounded-full ${
                      task.priority === 'high' ? 'bg-startup-red' :
                      task.priority === 'medium' ? 'bg-startup-gold' : 'bg-startup-gray'
                    }`} />
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-startup-teal animate-pulse" />
                      <span className="text-startup-navy font-medium">
                        {task.title}
                      </span>
                    </div>
                  </div>
                  <div className="text-startup-teal text-sm font-medium">
                    Due Today
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Overdue Tasks Section */}
      {overdueTasks.length > 0 && (
          <div className="card-startup animate-fade-in border-2 border-startup-red/30 bg-gradient-to-r from-startup-red/5 to-startup-red/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-startup-navy flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2 text-startup-red" />
                Overdue Tasks
              </h3>
              <span className="bg-startup-red text-white px-3 py-1 rounded-full text-sm font-medium animate-pulse">
                {overdueTasks.length} overdue
              </span>
            </div>
            
            <div className="space-y-3">
              {overdueTasks.slice(0, 5).map((task, index) => (
                <div 
                  key={task.id}
                  className="flex items-center justify-between p-3 bg-white/50 border border-startup-red/20 rounded-xl hover:shadow-md hover:border-startup-red/40 transition-all duration-300 animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => toggleTask(task.id)}
                      className="w-5 h-5 rounded-full border-2 border-startup-red hover:bg-startup-red hover:border-startup-red flex items-center justify-center transition-colors group"
                    >
                      {task.completed && <CheckCircle className="w-3 h-3 text-white" />}
                      {!task.completed && <div className="w-2 h-2 rounded-full bg-startup-red opacity-0 group-hover:opacity-100 transition-opacity" />}
                    </button>
                    <div className={`w-3 h-3 rounded-full ${
                      task.priority === 'high' ? 'bg-startup-red' :
                      task.priority === 'medium' ? 'bg-startup-gold' : 'bg-startup-gray'
                    }`} />
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="w-4 h-4 text-startup-red animate-pulse" />
                      <span className="text-startup-navy font-medium">
                        {task.title}
                      </span>
                    </div>
                  </div>
                  <div className="text-startup-red text-sm font-medium">
                    Overdue: {new Date(task.due_date!).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Tasks and Quick Links */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Pending Tasks */}
            <div className="card-startup animate-fade-in">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-startup-navy flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-startup-teal" />
                  Your Tasks
                </h3>
                <span className="bg-startup-teal/10 text-startup-teal px-3 py-1 rounded-full text-sm font-medium">
                  {pendingTasksCount} pending
                </span>
              </div>
              
              {/* Add Task Input */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  placeholder="Add a new task..."
                  className="flex-1 px-3 py-2 border border-startup-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-startup-teal"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTask()}
                />
                <Button onClick={handleAddTask} size="sm" className="bg-startup-teal text-white">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {pendingTasks.length > 0 ? pendingTasks.map((task, index) => {
                  const isToday = task.due_date && task.due_date.split('T')[0] === todayDateStr;
                  return (
                    <div 
                      key={task.id}
                      className={`flex items-center justify-between p-3 rounded-xl hover:shadow-md transition-all duration-300 animate-fade-in ${
                        isToday ? 'bg-blue-50 border border-blue-200' : 'bg-startup-slate'
                      }`}
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => toggleTask(task.id)}
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                            task.completed 
                              ? 'bg-startup-teal border-startup-teal' 
                              : 'border-startup-gray hover:border-startup-teal'
                          }`}
                        >
                          {task.completed && <CheckCircle className="w-3 h-3 text-white" />}
                        </button>
                        <div className={`w-3 h-3 rounded-full ${
                          task.priority === 'high' ? 'bg-startup-red' :
                          task.priority === 'medium' ? 'bg-startup-gold' : 'bg-startup-gray'
                        }`} />
                        <div className="flex items-center space-x-2">
                          {isToday && <Clock className="w-4 h-4 text-blue-600" />}
                          <span className={`text-startup-navy font-medium ${
                            task.completed ? 'line-through opacity-60' : ''
                          }`}>
                            {task.title}
                          </span>
                        </div>
                      </div>
                      <div className="text-startup-gray text-sm">
                        {isToday ? 'Due Today' : (task.category || 'General')}
                      </div>
                    </div>
                  );
                }) : (
                  <div className="text-center py-8 text-startup-gray">
                    <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium mb-1">No tasks yet!</p>
                    <p className="text-sm">Add your first task above to get started.</p>
                  </div>
                )}
              </div>
              
              {allTasksWithDaily.length > 5 && (
                <button
                  className="btn-startup-primary w-full mt-4"
                  onClick={() => setShowAllTasks(true)}
                >
                  View All {allTasksWithDaily.length} Tasks
                </button>
              )}
            </div>

            {/* Quick Links - Clean Card Style */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-6">Quick Links</h3>
              <div className="grid grid-cols-2 gap-4">
                {quickLinks.map((link) => (
                  <a
                    key={link.title}
                    href={link.href}
                    className="flex items-start p-4 rounded-lg border border-gray-100 hover:shadow-md transition-shadow duration-200"
                  >
                    <div className={`p-2 rounded-full ${link.iconBg} mr-3`}>
                      <link.icon className={`w-5 h-5 ${link.iconColor}`} />
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-gray-900">{link.title}</div>
                      <div className="text-sm text-gray-500">{link.description}</div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Finance & Notifications */}
          <div className="space-y-6">
            {/* Finance Card */}
            <FinanceCard 
              revenue={revenue}
              expenses={expenses}
              profit={revenue - expenses}
              revenueChange={revenueChange}
              expensesChange={expensesChange}
              onViewReport={handleViewReport}
            />

            {/* Notifications */}
            <div className="card-startup animate-fade-in">
              <h3 className="text-xl font-semibold text-startup-navy flex items-center mb-4">
                <Bell className="w-5 h-5 mr-2 text-startup-teal" />
                Notifications
              </h3>
              
              <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                {/* Social Media Reminders */}
                {pendingSocialActivities.length > 0 && (
                  <div className="p-3 rounded-xl bg-purple-50 border border-purple-200 animate-fade-in">
                    <div className="flex items-center mb-2">
                      <Share2 className="w-4 h-4 mr-2 text-purple-600" />
                      <p className="text-purple-800 text-sm font-medium">
                        📱 Social Media Reminder
                      </p>
                    </div>
                    <p className="text-purple-700 text-xs mb-2">
                      You have {pendingSocialActivities.length} pending social activities today
                    </p>
                    <button
                      onClick={() => navigate('/app/social-tracker')}
                      className="text-purple-600 text-xs hover:underline"
                    >
                      View Social Tracker →
                    </button>
                  </div>
                )}
                
                {notifications.slice(0, 8).map((notification, index) => (
                  <div 
                    key={notification.id}
                    className={`p-3 rounded-xl animate-fade-in ${notification.read_status ? 'bg-startup-slate' : 'bg-startup-gold/10 border border-startup-gold/30'}`}
                    style={{ animationDelay: `${(index + 1) * 0.1}s` }}
                    onClick={() => !notification.read_status && markNotificationRead(String(notification.id))}
                  >
                    <p className="text-startup-navy text-sm font-medium mb-1">
                      {notification.content}
                    </p>
                    <p className="text-startup-gray text-xs">{formatTime(notification.created_at)}</p>
                  </div>
                ))}
              </div>

              </div>

              {/* Task Completion Progress */}
              <div className="card-startup animate-fade-in">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-startup-navy">Overall Task Progress</h3>
                  <span className="text-startup-teal font-semibold">
                    {completedTasksCount}/{totalTasks} completed
                  </span>
                </div>
                <div className="bg-startup-slate rounded-full h-3 overflow-hidden">
                  <div 
                    className="bg-gradient-teal-gold h-full rounded-full animate-progress-fill"
                    style={{ '--progress-width': `${taskCompletionPercentage}%` } as React.CSSProperties}
                  />
                </div>
                <div className="flex justify-between text-sm text-startup-gray mt-2">
                  <span>Tasks completed</span>
                  <span>{Math.round(taskCompletionPercentage)}% done</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Task Notifications */}
      <DailyTaskNotification />

      {/* Cashpurr Notification Popup */}
      {showPopup && currentTask && (
        <CashpurrNotificationPopup
          task={currentTask}
          onClose={handleClosePopup}
        />
      )}
      
      {/* Tutorial System */}
      {showTutorial && (
        <TutorialSystem 
          onComplete={() => setShowTutorial(false)} 
          onSkip={() => setShowTutorial(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;
